import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { campaignAPI } from './campaignsAPI';
import { handleSnackbar } from '@/shared/ui';
import { isProd } from '@/shared/utils';
import { screenAPI } from '@/features/dooh/screen';
import {
  SetBasicDetailsPayload,
  SetAdvertiserCampaignPayload,
  CreateCampaignPayload,
  SetAdvertiserCampaignCreativesPayload,
  type CampaignAction,
} from '@/entities/campaign';
import type {
  CampaignSlotCreative,
  CampaignScreenSlot,
} from '@/entities/campaign';
import type { CampaignFilterDef } from './campaignsAPI';
import type {
  CampaignsState,
  OpenCampaignScreenEditModalPayload,
  OpenSlotEditModalPayload,
  CampaignScreenEditCreative,
} from './types';

const initialState: CampaignsState = {
  campaigns: {
    headers: [],
    rows: [],
    totalItems: 0,
    page: 0,
    rowsPerPage: 10,
    search: '',
  },
  campaignFilters: [],
  selectedCampaignFilters: {},
  selectedCampaign: null,
  loading: false,
  // Advertiser Campaign Initial State
  campaignDetails: {
    totalAudience: 0,
    targetAudience: 0,
    availableStates: [],
    stateLoading: false,
    campaignType: '',
    name: '',
    selectedBrand: null,
    campaignBudget: 0,
    campaignStartDate: '',
    campaignEndDate: '',
    industry: '',
    agency: '',
    source: '',
    timeSlots: [],
    currentTarget: null,
    city: '',
    state: '',
    selectedCityGroup: [], // Selected city group ID
    scheduling: undefined,
    targetGroups: [],
    selectedTouchpoints: [],
    targetGroupFilters: {
      gender: [],
      age: null,
      income: [],
      affinity: [],
    },
    creatives: [],
    useFallback: false,
    originalScreenSlots: {},
    selectedScreens: [],
    screensData: [],
    screensLoading: false,
    screensCount: 0,
  },
  advertiserCampaignCreatives: {
    showCustomCreativeModal: false,
    campaignType: '',
  },
  selectedContent: null,
  currentSelectedCampaign: null,
  // Slot Assignment Initial State
  slotAssignment: {
    open: false,
    slotIndex: 0,
    screenIndex: 0,
    assignToAllScreens: false,
    selectedSlots: [],
    globalCreativeType: 'image',
    allocatedCreatives: false,
  },
  deleteCampaignModal: {
    open: false,
    selectedCampaign: null,
  },
  playPauseCampaignModal: {
    open: false,
    selectedCampaign: null,
    action: null,
  },
  touchpoints: [],
  addTargetGroupModal: {
    open: false,
  },
  removeCityGroupModal: {
    open: false,
    cityGroupId: null,
  },
  typeChangeModal: {
    open: false,
    pendingCreativeType: null,
  },
  resetCreativesModal: {
    open: false,
  },
  campaignScreenEditModal: {
    isOpen: false,
    campaignId: null,
    screenId: null,
    source: null,
  },
  slotEditModal: {
    isOpen: false,
    slotIndex: null,
    slot: null,
    campaignId: '',
    deviceId: '',
    brandFolderId: '',
    campaignUseFallback: false,
    localSaveMode: false,
    lockedCreativeMode: 'image',
    initialCreatives: [],
    savedCreatives: null,
  },
};

// Async thunk for fetching campaigns
export const getCampaigns = createAsyncThunk(
  'campaigns/getCampaigns',
  async (
    { filters, search }: { filters: Record<string, any>; search: string },
    { dispatch, getState }
  ) => {
    try {
      const state = getState() as any;
      const { page, rowsPerPage } = state.dooh.campaigns.campaigns;

      const response = await campaignAPI.fetchCampaigns({
        filters,
        search,
        page,
        rowsPerPage,
      });

      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch campaigns';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

// Async thunk for fetching campaign filters
export const getCampaignFilters = createAsyncThunk(
  'campaigns/getCampaignFilters',
  async (_, { dispatch }) => {
    try {
      const response = await campaignAPI.fetchCampaignFilters();
      return response;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch campaign filters';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

// Custom createCampaign with data transformation
export const createCampaign = (payload: CreateCampaignPayload) => {
  return async (dispatch: any, getState: any) => {
    try {
      await campaignAPI.createCampaign(payload);
      const updatedRows = [
        ...getState().dooh.campaigns.campaigns.rows,
        payload,
      ];
      dispatch(
        setAdvertiserCampaign({
          rows: updatedRows,
        })
      );

      dispatch(
        getCampaigns({
          filters: {
            source: {
              key: 'source',
              type: 'terms',
              value: ['ADVERTISER'],
            },
          },
          search: '',
        })
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Something went wrong';
      dispatch(
        handleSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
    }
  };
};

// Get campaign by ID action
export const getCampaignById = createAsyncThunk(
  'campaigns/getCampaignById',
  async (payload: { id: string }, { rejectWithValue }) => {
    try {
      const response = await campaignAPI.getCampaignById(payload.id);

      if (response?.success) {
        return response.data;
      }
      return rejectWithValue('Failed to fetch campaign');
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch campaign'
      );
    }
  }
);

// Advertiser Campaign Filter Actions
export const fetchAdvertiserFilters = createAsyncThunk(
  'campaigns/fetchAdvertiserFilters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await campaignAPI.fetchAdvertiserFilters();
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch filters';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAdvertiserFiltersByState = createAsyncThunk(
  'campaigns/fetchAdvertiserFiltersByState',
  async (stateLabel: string, { rejectWithValue }) => {
    try {
      const filters = [
        {
          key: 'address.state',
          label: 'State',
          type: 'terms',
          value: [stateLabel],
        },
      ];
      if (isProd) {
        const data = await screenAPI.fetchScreenFilters({
          filters,
          filterType: 'advertiser',
        });
        return { success: true, data };
      } else {
        const response = await campaignAPI.fetchAdvertiserFilters(filters);
        return response.data;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch filters';
      return rejectWithValue(errorMessage);
    }
  }
);

export const setCampaignStatus = createAsyncThunk(
  'campaigns/setCampaignStatus',
  async (
    { data, campaignId }: { data: any; campaignId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await campaignAPI.setCampaignStatus(data, campaignId);
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update campaign status';
      return rejectWithValue(errorMessage);
    }
  }
);

// NEW: Fetch touchpoints with filters
const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    setCampaigns: (
      state,
      action: PayloadAction<Partial<CampaignsState['campaigns']>>
    ) => {
      state.campaigns = { ...state.campaigns, ...action.payload };
    },
    setCampaignFilters: (state, action: PayloadAction<CampaignFilterDef[]>) => {
      state.campaignFilters = action.payload;
    },
    setSelectedCampaignFilters: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.selectedCampaignFilters = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; rowsPerPage?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.campaigns.page = action.payload.page;
      }
      if (action.payload.rowsPerPage !== undefined) {
        state.campaigns.rowsPerPage = action.payload.rowsPerPage;
      }
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.campaigns.search = action.payload;
    },
    setSelectedCampaign: (state, action: PayloadAction<any>) => {
      state.selectedCampaign = action.payload;
    },
    // Advertiser Campaign Reducers
    setAdvertiserBasic: (
      state,
      action: PayloadAction<SetBasicDetailsPayload>
    ) => {
      const { field, value } = action.payload;
      (state.campaignDetails as any)[field] = value;
    },
    setCampaignDetails: (
      state,
      action: PayloadAction<Partial<Record<string, any>>>
    ) => {
      Object.entries(action.payload).forEach(([field, value]) => {
        (state.campaignDetails as any)[field] = value;
      });
    },
    setAdvertiserCampaign: (
      state,
      action: PayloadAction<SetAdvertiserCampaignPayload>
    ) => {
      const { headers, rows, totalItems, page, rowsPerPage } = action.payload;
      if (headers) state.campaigns.headers = headers;
      if (rows) state.campaigns.rows = rows;
      if (totalItems !== undefined) state.campaigns.totalItems = totalItems;
      if (page !== undefined) state.campaigns.page = page;
      if (rowsPerPage !== undefined) state.campaigns.rowsPerPage = rowsPerPage;
    },
    setSelectedContent: (state, action: PayloadAction<any>) => {
      state.selectedContent = action.payload;
    },
    resetCampaignData: state => {
      state.campaignDetails = initialState.campaignDetails;
      state.selectedContent = initialState.selectedContent;
      state.currentSelectedCampaign = initialState.currentSelectedCampaign;
      state.slotAssignment = initialState.slotAssignment;
    },
    setAdvertiserCampaignCreatives: (
      state,
      action: PayloadAction<SetAdvertiserCampaignCreativesPayload>
    ) => {
      const { type, open } = action.payload;
      state.advertiserCampaignCreatives = {
        showCustomCreativeModal: open,
        campaignType: type,
      };
    },
    setCurrentSelectedCampaign: (state, action: PayloadAction<any>) => {
      state.currentSelectedCampaign = action.payload;
    },
    setSelectedScreenSlots: (state, action: PayloadAction<any[]>) => {
      state.campaignDetails.selectedScreens = action.payload;
    },
    // Update screen slot assignments from drag-and-drop
    updateScreenSlotAssignments: (
      state,
      action: PayloadAction<{
        screenId: string;
        slotsData: Array<{ index: number; creatives: CampaignSlotCreative[] }>;
      }>
    ) => {
      const { screenId, slotsData } = action.payload;
      const screen = state.campaignDetails.selectedScreens.find(
        s => s.id === screenId
      );
      if (!screen) return;
      // Overwrite matching slots with provided creatives
      slotsData.forEach(({ index, creatives }) => {
        if (screen.slots && screen.slots[index]) {
          screen.slots[index].creatives = creatives;
        }
      });
    },
    // Restore previous slot assignments for undo
    restoreScreenSlots: (
      state,
      action: PayloadAction<{
        screenSnapshots: Array<{
          screenId: string;
          slots: CampaignScreenSlot[];
        }>;
      }>
    ) => {
      const { screenSnapshots } = action.payload;
      screenSnapshots.forEach(snapshot => {
        const screen = state.campaignDetails.selectedScreens.find(
          s => s.id === snapshot.screenId
        );
        if (!screen) return;
        if (screen.slots) {
          screen.slots = snapshot.slots;
        }
      });
    },
    // Slot Assignment Actions
    setSlotAssignmentModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        slotIndex?: number;
        screenIndex?: number;
        assignToAllScreens?: boolean;
        selectedSlots?: Array<{ screenIndex: number; slotIndex: number }>;
        initialAddMode?: 'image' | 'VAST' | 'WEB';
      }>
    ) => {
      state.slotAssignment.open = action.payload.open;

      if (typeof action.payload.screenIndex === 'number') {
        state.slotAssignment.screenIndex = action.payload.screenIndex;
      }

      if (typeof action.payload.slotIndex === 'number') {
        state.slotAssignment.slotIndex = action.payload.slotIndex;
      }

      if (action.payload.assignToAllScreens !== undefined) {
        state.slotAssignment.assignToAllScreens =
          action.payload.assignToAllScreens;
      }

      if (action.payload.selectedSlots !== undefined) {
        state.slotAssignment.selectedSlots = action.payload.selectedSlots;
      }
    },

    resetSlotAssignment: state => {
      state.slotAssignment = {
        open: false,
        slotIndex: 0,
        screenIndex: 0,
        assignToAllScreens: false,
        selectedSlots: [],
        globalCreativeType: 'image',
        allocatedCreatives: false,
      };
    },
    setGlobalCreativeType: (
      state,
      action: PayloadAction<'image' | 'VAST' | 'WEB'>
    ) => {
      state.slotAssignment.globalCreativeType = action.payload;
    },
    setAllocatedCreativesFlag: (state, action: PayloadAction<boolean>) => {
      state.slotAssignment.allocatedCreatives = action.payload;
    },
    resetAllSelectedSlots: state => {
      // Clear all creatives from ALL slots across all screens
      state.campaignDetails.selectedScreens?.forEach(screen => {
        screen.slots?.forEach(slot => {
          slot.creatives = [];
        });
      });
    },

    setSelectedScreens: (state, action: PayloadAction<any[]>) => {
      state.campaignDetails.selectedScreens = action.payload;
    },
    resetTargetGroupFilters: state => {
      state.campaignDetails.targetGroupFilters = {
        gender: [],
        age: null,
        income: [],
        affinity: [],
      };
    },
    setDeleteCampaignModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedCampaign: any | null;
      }>
    ) => {
      state.deleteCampaignModal = action.payload;
    },
    setPlayPauseCampaignModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedCampaign: any | null;
        action: CampaignAction;
      }>
    ) => {
      state.playPauseCampaignModal = action.payload;
    },
    setAddTargetGroupModal: (
      state,
      action: PayloadAction<{ open: boolean }>
    ) => {
      state.addTargetGroupModal.open = action.payload.open;
    },
    setRemoveCityGroupModal: (
      state,
      action: PayloadAction<{ open: boolean; cityGroupId: string | null }>
    ) => {
      state.removeCityGroupModal = action.payload;
    },
    setTypeChangeModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        pendingCreativeType: 'image' | 'VAST' | 'WEB' | null;
      }>
    ) => {
      state.typeChangeModal = action.payload;
    },
    setResetCreativesModal: (
      state,
      action: PayloadAction<{ open: boolean }>
    ) => {
      state.resetCreativesModal = action.payload;
    },
    openCampaignScreenEditModal: (
      state,
      action: PayloadAction<OpenCampaignScreenEditModalPayload>
    ) => {
      state.campaignScreenEditModal.isOpen = true;
      state.campaignScreenEditModal.campaignId = action.payload.campaignId;
      state.campaignScreenEditModal.screenId = action.payload.screenId;
      state.campaignScreenEditModal.source = action.payload.source;
      state.campaignScreenEditModal.campaignName = action.payload.campaignName;
      state.campaignScreenEditModal.screenName = action.payload.screenName;
    },
    closeCampaignScreenEditModal: state => {
      state.campaignScreenEditModal = {
        isOpen: false,
        campaignId: null,
        screenId: null,
        source: null,
      };
      state.slotEditModal = {
        isOpen: false,
        slotIndex: null,
        slot: null,
        campaignId: '',
        deviceId: '',
        brandFolderId: '',
        campaignUseFallback: false,
        localSaveMode: false,
        lockedCreativeMode: 'image',
        initialCreatives: [],
        savedCreatives: null,
      };
    },
    resetCampaignScreenEditState: state => {
      state.campaignScreenEditModal = {
        isOpen: false,
        campaignId: null,
        screenId: null,
        source: null,
      };
      state.slotEditModal = {
        isOpen: false,
        slotIndex: null,
        slot: null,
        campaignId: '',
        deviceId: '',
        brandFolderId: '',
        campaignUseFallback: false,
        localSaveMode: false,
        lockedCreativeMode: 'image',
        initialCreatives: [],
        savedCreatives: null,
      };
    },
    openSlotEditModal: (
      state,
      action: PayloadAction<OpenSlotEditModalPayload>
    ) => {
      state.slotEditModal = {
        ...action.payload,
        campaignUseFallback: action.payload.campaignUseFallback ?? false,
        isOpen: true,
        savedCreatives: null,
      };
    },
    closeSlotEditModal: state => {
      state.slotEditModal.isOpen = false;
    },
    saveSlotEditCreatives: (
      state,
      action: PayloadAction<CampaignScreenEditCreative[]>
    ) => {
      state.slotEditModal.savedCreatives = action.payload;
      state.slotEditModal.isOpen = false;
    },
    clearSlotEditResult: state => {
      state.slotEditModal.savedCreatives = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCampaigns.pending, state => {
        state.loading = true;
      })
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = { ...state.campaigns, ...action.payload };
      })
      .addCase(getCampaigns.rejected, state => {
        state.loading = false;
      })
      .addCase(getCampaignFilters.pending, state => {
        state.loading = true;
      })
      .addCase(getCampaignFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.campaignFilters = action.payload;
      })
      .addCase(getCampaignFilters.rejected, state => {
        state.loading = false;
      })
      // Advertiser Campaign Cases
      .addCase(fetchAdvertiserFilters.pending, state => {
        state.loading = true;
      })
      .addCase(fetchAdvertiserFilters.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          state.campaignDetails.availableStates = action.payload.data;
        }
      })
      .addCase(fetchAdvertiserFilters.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchAdvertiserFiltersByState.pending, state => {
        state.campaignDetails.stateLoading = true;
      })
      .addCase(fetchAdvertiserFiltersByState.fulfilled, (state, action) => {
        state.campaignDetails.stateLoading = false;
        if (action.payload?.success) {
          const existing = state.campaignDetails.availableStates || [];
          const incoming = action.payload.data || [];
          const incomingCity = incoming.find(
            (f: any) => f.key === 'address.city'
          );

          if (incomingCity) {
            let replaced = false;
            const merged = existing.map((item: any) => {
              if (item.key === 'address.city') {
                replaced = true;
                return { ...item, values: incomingCity.values || [] };
              }
              return item;
            });
            state.campaignDetails.availableStates = replaced
              ? merged
              : [...existing, incomingCity];
          }
        }
      })
      .addCase(fetchAdvertiserFiltersByState.rejected, state => {
        state.campaignDetails.stateLoading = false;
      })
      .addCase(setCampaignStatus.pending, state => {
        state.loading = true;
      })
      .addCase(setCampaignStatus.fulfilled, () => {
        // Status updated successfully
      })
      .addCase(setCampaignStatus.rejected, state => {
        state.loading = false;
      });
  },
});

export const {
  setCampaigns,
  setCampaignFilters,
  setSelectedCampaignFilters,
  setLoading,
  setPagination,
  setSearch,
  setSelectedCampaign,
  // Advertiser Campaign Actions
  setAdvertiserBasic,
  setCampaignDetails,
  setAdvertiserCampaign,
  setSelectedContent,
  resetCampaignData,
  setAdvertiserCampaignCreatives,
  setCurrentSelectedCampaign,
  setSelectedScreenSlots,
  // Slot Assignment Actions
  setSlotAssignmentModal,
  resetSlotAssignment,
  setGlobalCreativeType,
  setAllocatedCreativesFlag,
  updateScreenSlotAssignments,
  restoreScreenSlots,
  resetAllSelectedSlots,
  setSelectedScreens,
  resetTargetGroupFilters,
  setDeleteCampaignModal,
  setPlayPauseCampaignModal,
  setAddTargetGroupModal,
  setRemoveCityGroupModal,
  setTypeChangeModal,
  setResetCreativesModal,
  openCampaignScreenEditModal,
  closeCampaignScreenEditModal,
  resetCampaignScreenEditState,
  openSlotEditModal,
  closeSlotEditModal,
  saveSlotEditCreatives,
  clearSlotEditResult,
} = campaignsSlice.actions;

export default campaignsSlice.reducer;
